// Offline Authentication Service
// Handles authentication logic without Supabase calls when offline

import { Session, User } from '@supabase/supabase-js';
import { UserRole } from '../hooks/useOfflineUserRole';
import {
  CachedSession,
  CachedUser,
  getOfflineDB,
  UserProfile,
} from './offlineDB';
import { offlineSupabase } from './offlineSupabaseProxy';
import { supabase } from './supabaseClient';

export interface AuthState {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isOnline: boolean;
  isOfflineMode: boolean;
  lastSync: number | null;
}

export interface AuthResult {
  user: User | null;
  session: Session | null;
  error: Error | null;
  fromCache: boolean;
}

export class OfflineAuthService {
  private static instance: OfflineAuthService;
  private isOnline: boolean = navigator.onLine;
  private authStateCallbacks: ((state: AuthState) => void)[] = [];
  private onlineDebounceTimer: NodeJS.Timeout | null = null;
  private offlineDebounceTimer: NodeJS.Timeout | null = null;

  // Reload loop detection
  private networkTransitions: number[] = []; // Timestamps of recent transitions
  private readonly MAX_TRANSITIONS = 3; // Max transitions allowed
  private readonly TRANSITION_WINDOW = 5000; // Time window in ms (5 seconds)

  private currentAuthState: AuthState = {
    user: null,
    session: null,
    isLoading: true,
    isOnline: navigator.onLine,
    isOfflineMode: false,
    lastSync: null,
  };

  private constructor() {
    this.setupOnlineOfflineListeners();
    this.initializeDatabase();
  }

  static getInstance(): OfflineAuthService {
    if (!OfflineAuthService.instance) {
      OfflineAuthService.instance = new OfflineAuthService();
    }
    return OfflineAuthService.instance;
  }

  /**
   * Initialize the offline database
   */
  private async initializeDatabase(userId?: string): Promise<void> {
    try {
      if (userId) {
        const offlineDB = getOfflineDB(userId);
        await offlineDB.init();
        console.log('[OfflineAuth] Database initialized for user:', userId);
      }
    } catch (error) {
      console.error('[OfflineAuth] Failed to initialize database:', error);
    }
  }

  /**
   * Detect if we're in a reload loop based on network transition frequency
   */
  private detectReloadLoop(): boolean {
    const now = Date.now();

    // Record this transition
    this.networkTransitions.push(now);

    // Remove transitions outside the time window
    this.networkTransitions = this.networkTransitions.filter(
      timestamp => now - timestamp < this.TRANSITION_WINDOW
    );

    // Check if we've exceeded the max transitions
    if (this.networkTransitions.length > this.MAX_TRANSITIONS) {
      console.warn(
        `[OfflineAuth] Reload loop detected: ${this.networkTransitions.length} network transitions in ${this.TRANSITION_WINDOW}ms. Throttling state updates.`
      );
      return true;
    }

    return false;
  }

  /**
   * Setup online/offline event listeners with debouncing to prevent excessive rerenders
   */
  private setupOnlineOfflineListeners(): void {
    window.addEventListener('online', () => {
      // Debounce online event to prevent rapid state changes
      if (this.onlineDebounceTimer) {
        clearTimeout(this.onlineDebounceTimer);
      }

      this.onlineDebounceTimer = setTimeout(() => {
        if (this.isOnline) return; // Already online, skip update

        // Check for reload loop
        if (this.detectReloadLoop()) {
          console.warn(
            '[OfflineAuth] Skipping online state update due to reload loop detection'
          );
          return;
        }

        console.log('[OfflineAuth] Went online');
        this.isOnline = true;
        this.updateAuthState({ isOnline: true, isOfflineMode: false });
        this.syncWithSupabase();
      }, 500); // 500ms debounce
    });

    window.addEventListener('offline', () => {
      // Debounce offline event to prevent rapid state changes
      if (this.offlineDebounceTimer) {
        clearTimeout(this.offlineDebounceTimer);
      }

      this.offlineDebounceTimer = setTimeout(() => {
        if (!this.isOnline) return; // Already offline, skip update

        // Check for reload loop
        if (this.detectReloadLoop()) {
          console.warn(
            '[OfflineAuth] Skipping offline state update due to reload loop detection'
          );
          return;
        }

        console.log('[OfflineAuth] Went offline');
        this.isOnline = false;
        this.updateAuthState({ isOnline: false, isOfflineMode: true });
      }, 500); // 500ms debounce
    });
  }

  /**
   * Get current session - online or offline
   */
  async getSession(): Promise<AuthResult> {
    try {
      if (this.isOnline) {
        return await this.getOnlineSession();
      } else {
        return await this.getOfflineSession();
      }
    } catch (error) {
      console.error('[OfflineAuth] Failed to get session:', error);
      return {
        user: null,
        session: null,
        error: error as Error,
        fromCache: false,
      };
    }
  }

  /**
   * Get session from Supabase (online)
   */
  private async getOnlineSession(): Promise<AuthResult> {
    try {
      const { data, error } = await supabase.auth.getSession();

      if (error) {
        throw error;
      }

      if (data.session && data.session.user) {
        // Cache the user data for offline use
        await this.cacheUserData(data.session.user, data.session);

        return {
          user: data.session.user,
          session: data.session,
          error: null,
          fromCache: false,
        };
      }

      return {
        user: null,
        session: null,
        error: null,
        fromCache: false,
      };
    } catch (error) {
      console.error('[OfflineAuth] Online session check failed:', error);

      // Fallback to offline session if online fails
      return await this.getOfflineSession();
    }
  }

  /**
   * Get session from IndexedDB cache (offline)
   */
  private async getOfflineSession(userId?: string): Promise<AuthResult> {
    try {
      if (!userId) {
        // Try to find the last logged-in user from any available database
        userId = await this.getLastLoggedInUserId();
        if (!userId) {
          console.log('[OfflineAuth] No cached user found');
          return {
            user: null,
            session: null,
            error: null,
            fromCache: true,
          };
        }
      }

      const offlineDB = getOfflineDB(userId);
      const cachedUser = await offlineDB.getUser(userId);

      if (cachedUser && (await offlineDB.isSessionValid(cachedUser.id))) {
        console.log(
          '[OfflineAuth] Using cached session for:',
          cachedUser.email
        );

        const session = this.createSessionFromCache(cachedUser);

        return {
          user: cachedUser.userData,
          session: session,
          error: null,
          fromCache: true,
        };
      }

      console.log('[OfflineAuth] No valid cached session found');
      return {
        user: null,
        session: null,
        error: null,
        fromCache: true,
      };
    } catch (error) {
      console.error('[OfflineAuth] Failed to get offline session:', error);
      return {
        user: null,
        session: null,
        error: error as Error,
        fromCache: true,
      };
    }
  }

  /**
   * Cache user data for offline use
   */
  private async cacheUserData(user: User, session: Session): Promise<void> {
    try {
      // Get user profile from Supabase (using altzero schema)
      const { data: profileData } = await offlineSupabase
        .schema('altzero')
        .from('base_profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      // Get user role
      const role = profileData?.role || 'initial';

      const cachedSession: CachedSession = {
        access_token: session.access_token,
        refresh_token: session.refresh_token || '',
        expires_at: session.expires_at || 0,
        user_id: user.id,
      };

      const cachedUser: CachedUser = {
        id: user.id,
        email: user.email || '',
        userData: user,
        profile: profileData || null,
        role: role as UserRole,
        session: cachedSession,
        lastSync: Date.now(),
        isOfflineEnabled: true,
        offlineCapabilities: ['dashboard', 'profile', 'settings'],
      };

      const offlineDB = getOfflineDB(user.id);
      await offlineDB.storeUser(cachedUser);
      console.log('[OfflineAuth] User data cached successfully:', user.id);

      // Store this as the last logged-in user
      this.storeLastLoggedInUserId(user.id);

      this.updateAuthState({ lastSync: Date.now() });
    } catch (error) {
      console.error('[OfflineAuth] Failed to cache user data:', error);
    }
  }

  /**
   * Get the last logged-in user ID from IndexedDB
   */
  private async getLastLoggedInUserId(): Promise<string | null> {
    try {
      // Check if there's a stored last user ID in localStorage
      const lastUserId = localStorage.getItem('altzero_last_user_id');
      if (lastUserId) {
        // Verify this user exists in IndexedDB
        const offlineDB = getOfflineDB(lastUserId);
        const cachedUser = await offlineDB.getUser(lastUserId);
        if (cachedUser) {
          return lastUserId;
        }
      }

      // If no stored ID or user doesn't exist, try to find any cached user
      // This is a fallback - we'll check common database names
      const commonUserIds = await this.findAvailableUserDatabases();
      if (commonUserIds.length > 0) {
        // Return the most recently used one
        return commonUserIds[0];
      }

      return null;
    } catch (error) {
      console.error(
        '[OfflineAuth] Failed to get last logged-in user ID:',
        error
      );
      return null;
    }
  }

  /**
   * Find available user databases in IndexedDB
   */
  private async findAvailableUserDatabases(): Promise<string[]> {
    try {
      // This is a simplified approach - in a real implementation,
      // you might want to maintain a registry of user databases
      const databases = await indexedDB.databases();
      const userDatabases = databases
        .filter(db => db.name?.startsWith('user_'))
        .map(db => db.name!.replace('user_', ''))
        .filter(id => id.length > 0);

      return userDatabases;
    } catch (error) {
      console.error('[OfflineAuth] Failed to find user databases:', error);
      return [];
    }
  }

  /**
   * Store the last logged-in user ID
   */
  private storeLastLoggedInUserId(userId: string): void {
    try {
      localStorage.setItem('altzero_last_user_id', userId);
    } catch (error) {
      console.error('[OfflineAuth] Failed to store last user ID:', error);
    }
  }

  /**
   * Create session object from cached data
   */
  private createSessionFromCache(cachedUser: CachedUser): Session {
    if (!cachedUser.session) {
      throw new Error('No cached session available');
    }

    return {
      access_token: cachedUser.session.access_token,
      refresh_token: cachedUser.session.refresh_token,
      expires_in: Math.max(
        0,
        cachedUser.session.expires_at - Date.now() / 1000
      ),
      expires_at: cachedUser.session.expires_at,
      token_type: 'bearer',
      user: cachedUser.userData,
    };
  }

  /**
   * Sign in with password - handles online/offline
   */
  async signIn(email: string, password: string): Promise<AuthResult> {
    if (!this.isOnline) {
      return {
        user: null,
        session: null,
        error: new Error(
          'Cannot sign in while offline. Please connect to the internet.'
        ),
        fromCache: false,
      };
    }

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        return {
          user: null,
          session: null,
          error: error,
          fromCache: false,
        };
      }

      if (data.session && data.user) {
        // Cache user data for offline use
        await this.cacheUserData(data.user, data.session);

        return {
          user: data.user,
          session: data.session,
          error: null,
          fromCache: false,
        };
      }

      return {
        user: null,
        session: null,
        error: new Error('Sign in failed'),
        fromCache: false,
      };
    } catch (error) {
      return {
        user: null,
        session: null,
        error: error as Error,
        fromCache: false,
      };
    }
  }

  /**
   * Sign out - handles online/offline
   */
  async signOut(): Promise<void> {
    try {
      if (this.isOnline) {
        await supabase.auth.signOut();
      }

      // Clear offline cache for the current user
      const lastUserId = localStorage.getItem('altzero_last_user_id');
      if (lastUserId) {
        const offlineDB = getOfflineDB(lastUserId);
        await offlineDB.clearAll();
        console.log(
          '[OfflineAuth] Signed out and cleared cache for user:',
          lastUserId
        );
      }

      // Clear the stored user ID
      localStorage.removeItem('altzero_last_user_id');

      this.updateAuthState({
        user: null,
        session: null,
        lastSync: null,
      });
    } catch (error) {
      console.error('[OfflineAuth] Failed to sign out:', error);
    }
  }

  /**
   * Get user role - offline aware
   */
  async getUserRole(userId: string): Promise<UserRole | null> {
    try {
      if (this.isOnline) {
        // Try to get from Supabase using RPC function (bypasses RLS)
        const { data, error } = await supabase.rpc('get_user_profile', {
          user_id: userId,
        });

        if (!error && data && data.length > 0) {
          const role = data[0].role;
          // Update cache with latest role
          const offlineDB = getOfflineDB(userId);
          await offlineDB.updateUserRole(userId, role as UserRole);
          return role as UserRole;
        }
      }

      // Fallback to offline cache
      const offlineDB = getOfflineDB(userId);
      const cachedUser = await offlineDB.getUser(userId);
      return cachedUser?.role || null;
    } catch (error) {
      console.error('[OfflineAuth] Failed to get user role:', error);

      // Fallback to cache on error
      const offlineDB = getOfflineDB(userId);
      const cachedUser = await offlineDB.getUser(userId);
      return cachedUser?.role || null;
    }
  }

  /**
   * Get user profile - offline aware
   */
  async getUserProfile(userId: string): Promise<UserProfile | null> {
    try {
      if (this.isOnline) {
        // Try to get from Supabase using RPC function (bypasses RLS)
        const { data, error } = await supabase.rpc('get_user_profile', {
          user_id: userId,
        });

        if (!error && data && data.length > 0) {
          const profileData = data[0];
          // Update cache with latest profile
          const offlineDB = getOfflineDB(userId);
          await offlineDB.updateUserProfile(userId, profileData);
          return profileData;
        }
      }

      // Fallback to offline cache
      const offlineDB = getOfflineDB(userId);
      const cachedUser = await offlineDB.getUser(userId);
      return cachedUser?.profile || null;
    } catch (error) {
      console.error('[OfflineAuth] Failed to get user profile:', error);

      // Fallback to cache on error
      const offlineDB = getOfflineDB(userId);
      const cachedUser = await offlineDB.getUser(userId);
      return cachedUser?.profile || null;
    }
  }

  /**
   * Sync with Supabase when coming back online
   */
  private async syncWithSupabase(): Promise<void> {
    try {
      console.log('[OfflineAuth] Syncing with Supabase...');

      // Get current session from Supabase
      const result = await this.getOnlineSession();

      if (result.user && result.session) {
        this.updateAuthState({
          user: result.user,
          session: result.session,
          isLoading: false,
        });
      }
    } catch (error) {
      console.error('[OfflineAuth] Sync failed:', error);
    }
  }

  /**
   * Subscribe to auth state changes
   * Note: Callback is NOT called immediately to prevent re-render loops.
   * Components should call getCurrentState() explicitly if they need the current state on mount.
   */
  onAuthStateChange(callback: (state: AuthState) => void): () => void {
    this.authStateCallbacks.push(callback);

    // Defer immediate callback execution to prevent re-render loops
    // Components can call getCurrentState() explicitly if needed

    // Return unsubscribe function
    return () => {
      const index = this.authStateCallbacks.indexOf(callback);
      if (index > -1) {
        this.authStateCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Update auth state and notify callbacks only if values actually changed
   */
  private updateAuthState(updates: Partial<AuthState>): void {
    const prevState = { ...this.currentAuthState };
    const newState = { ...this.currentAuthState, ...updates };

    // Check if any values actually changed
    const hasChanged = Object.keys(updates).some(key => {
      const typedKey = key as keyof AuthState;
      return prevState[typedKey] !== newState[typedKey];
    });

    if (!hasChanged) {
      return; // Skip update if nothing changed
    }

    this.currentAuthState = newState;

    // Notify callbacks
    this.authStateCallbacks.forEach(callback => {
      try {
        callback(this.currentAuthState);
      } catch (error) {
        console.error('[OfflineAuth] Error in auth state callback:', error);
      }
    });
  }

  /**
   * Get current auth state
   */
  getCurrentState(): AuthState {
    return { ...this.currentAuthState };
  }

  /**
   * Check if user can access app offline
   */
  async canAccessOffline(userId: string): Promise<boolean> {
    const offlineDB = getOfflineDB(userId);
    const cachedUser = await offlineDB.getUser(userId);
    return cachedUser?.isOfflineEnabled || false;
  }

  /**
   * Get offline capabilities
   */
  async getOfflineCapabilities(userId: string): Promise<string[]> {
    const offlineDB = getOfflineDB(userId);
    const cachedUser = await offlineDB.getUser(userId);
    return cachedUser?.offlineCapabilities || [];
  }
}

// Export singleton instance
export const offlineAuth = OfflineAuthService.getInstance();
